import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Download, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState("checking");
  const [order, setOrder] = useState(null);

  const poll = useCallback(async (attempt) => {
    if (attempt > 8) { setState("timeout"); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid" && data.access_token) {
        const o = await api.get(`/orders/${data.access_token}`);
        setOrder(o.data); setState("paid"); clear();
        return;
      }
      if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      setTimeout(() => poll(attempt + 1), 2000);
    } catch { setTimeout(() => poll(attempt + 1), 2000); }
  }, [sessionId, clear]);

  useEffect(() => { if (sessionId) poll(0); else setState("failed"); }, [sessionId, poll]);

  const download = (token, fileId, filename) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/orders/${token}/download/${fileId}`;
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-24 text-center">
      {state === "checking" && (<><Loader2 className="w-12 h-12 text-[#3B1F6E] animate-spin mx-auto mb-4" /><p className="text-slate-500">Confirming your payment...</p></>)}
      {state === "failed" && (<><XCircle className="w-14 h-14 text-red-500 mx-auto mb-4" /><h1 className="font-serif-display text-3xl font-semibold">Payment not completed</h1><Link to="/cart" className="inline-block mt-6 text-[#3B1F6E] font-semibold">Return to cart</Link></>)}
      {state === "timeout" && (<><Loader2 className="w-12 h-12 text-slate-400 mx-auto mb-4" /><p className="text-slate-500">Still processing. Refresh in a moment.</p></>)}
      {state === "paid" && order && (
        <div data-testid="order-confirmation">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-5" />
          <h1 className="font-serif-display text-4xl font-bold text-[#0A0F1D] tracking-tight">Thank you!</h1>
          <p className="text-slate-500 mt-3">Your purchase is ready. Download your files below.</p>
          <div className="mt-10 text-left space-y-5">
            {order.items.map((it) => (
              <div key={it.product_key} className="bg-white rounded-2xl border border-slate-200 p-6">
                <p className="font-serif-display text-xl font-semibold text-[#0A0F1D] mb-4">{it.title}</p>
                <div className="space-y-2">
                  {it.files.map((f) => (
                    <button key={f.file_id} data-testid={`download-${f.file_id}`} onClick={() => download(order.access_token, f.file_id, f.filename)}
                      className="w-full flex items-center justify-between bg-[#3B1F6E]/5 hover:bg-[#3B1F6E]/10 rounded-xl px-4 py-3 text-left transition-colors">
                      <span className="text-sm font-medium text-[#2E1065]">{f.filename}</span>
                      <Download className="w-4 h-4 text-[#D97706]" />
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <Link to="/shop" className="inline-block mt-10 text-[#3B1F6E] font-semibold">Continue shopping</Link>
        </div>
      )}
    </div>
  );
}
